<script setup>
// Required Components
import BaseButton from '@/components/Base/Button/BaseButton'
import SearchField from '@/components/Base/Fields/SearchField'
import { computed, ref, watch } from 'vue'
import store from '@/store'

const props = defineProps({
  headers: {
    type: Array,
    required: true
  },
  items: {
    type: Array,
    required: true
  },
  readOnly: {
    type: Boolean,
    default: false
  },
  hasColor: {
    type: Boolean,
    default: false
  },
  updateRowName: {
    type: Function,
    required: false,
    default: () => {}
  },
  toggleIconsModal: {
    type: Function,
    required: false,
    default: () => {}
  },
  deleteRow: {
    type: Function,
    required: false,
    default: () => {}
  },
  invertColor: {
    type: Boolean,
    default: true
  },
  inputPlaceHolder: {
    type: String,
    default: 'New Category'
  },
  sortFunction: {
    type: Function,
    required: true,
    default: () => {}
  },
  fullWidth: {
    type: Boolean,
    default: true
  },
  selectId : {
    type: String,
    default: ''
  },
  showPagination: {
    type: Boolean,
    default: false
  },
  itemsPerPage: {
    type: Number,
    default: 10
  }
})

const searchValue = computed(() => {
  return store.getters.getSearchParticipants
})

const searchTeam = computed({
  get() {
    return searchValue.value
  },
  set(newValue) {
    store.dispatch('setSearchParticipants', newValue)
  }
})

// Pagination state and computations
const currentPage = ref(1)
const totalPages = computed(() => {
  const total = props.items?.length || 0
  return Math.max(1, Math.ceil(total / props.itemsPerPage))
})

// Build a compressed pagination range like: 1 ... 7 8 9 10
const makeRange = (start, end) => {
  const arr = []
  for (let i = start; i <= end; i++) arr.push(i)
  return arr
}

const pages = computed(() => {
  const total = totalPages.value
  const current = currentPage.value
  const siblingCount = 1

  // Total numeric items to show including current and siblings plus first/last and two ellipses
  const totalPageNumbers = siblingCount * 2 + 5

  if (total <= totalPageNumbers) {
    return makeRange(1, total)
  }

  const leftSiblingIndex = Math.max(current - siblingCount, 1)
  const rightSiblingIndex = Math.min(current + siblingCount, total)

  const showLeftDots = leftSiblingIndex > 2
  const showRightDots = rightSiblingIndex < total - 1

  if (!showLeftDots && showRightDots) {
    const leftRange = makeRange(1, 3 + 2 * siblingCount)
    return [...leftRange, '...', total]
  }

  if (showLeftDots && !showRightDots) {
    const rightRange = makeRange(total - (3 + 2 * siblingCount) + 1, total)
    return [1, '...', ...rightRange]
  }

  if (showLeftDots && showRightDots) {
    const middleRange = makeRange(leftSiblingIndex, rightSiblingIndex)
    return [1, '...', ...middleRange, '...', total]
  }

  // Fallback (shouldn't hit)
  return makeRange(1, total)
})

const paginatedItems = computed(() => {
  if (!props.showPagination) return props.items
  const start = (currentPage.value - 1) * props.itemsPerPage
  return props.items.slice(start, start + props.itemsPerPage)
})

function goToPage(page) {
  const p = Math.min(Math.max(1, page), totalPages.value)
  currentPage.value = p
}

function prevPage() {
  if (currentPage.value > 1) currentPage.value -= 1
}

function nextPage() {
  if (currentPage.value < totalPages.value) currentPage.value += 1
}

watch(
  () => [props.items?.length, props.itemsPerPage],
  () => {
    // Ensure currentPage stays within bounds when data or page size changes
    if (currentPage.value > totalPages.value) {
      currentPage.value = totalPages.value
    }
  }
)
</script>

<template>
  <table class="table" :class="{ 'w-full': fullWidth }">
    <thead>
    <tr>
      <th v-for="header in headers"
          :key="header.text"
          class="table-headers"
          :class="header.class ?? ''">
        {{header.text}}
        <base-button v-if="header.sortable"
                     :id="`table-${header.value}}`"
                     :name="`table-${header.value}}`"
                     :text="header.value"
                     btn-type="text"
                     class-name="table-sortBtn"
                     :functions="() => sortFunction(header.value)">
          <template #contents>
            <i class="fa-solid fa-arrow-down-arrow-up"></i>
          </template>
        </base-button>
        <search-field v-if="header.searchable"
                      v-model="searchTeam"
                      input-id="dashboard-Search"
                      input-name="dashboard-Search"
                      input-place-holder="Search"
                      class="table-searchable">
        </search-field>
      </th>
    </tr>
    </thead>
    <tbody>
    <tr v-for="item in paginatedItems"
        :key="item.id"
        class="table-items"
        :class="{'table-items-selected': selectId === item.id}">
      <slot name="content-column" :item="item"></slot>
      <td>
        <slot name="action-column" :item="item"></slot>
      </td>
    </tr>
    </tbody>
  </table>
  <div v-if="showPagination && totalPages > 1" class="table-pagination">
    <base-button
      id="table-prev"
      class-name="table-button"
      btn-type="tertiary"
      :functions="prevPage"
      :disabled="currentPage === 1"
      name="table-prev"
      text="Prev"
    >
      <template #contents>
        <i class="fa-solid fa-chevron-left"></i>
      </template>
    </base-button>

    <template v-for="p in pages" :key="`p-${p}-${currentPage}`">
      <span v-if="p === '...'" class="table-ellipsis">...</span>
      <base-button
        v-else
        :id="`table-page-${p}`"
        class-name="table-button"
        :btn-type="currentPage === p ? 'primary' : 'tertiary'"
        :functions="() => goToPage(p)"
        :name="`table-page-${p}`"
        :text="String(p)"
        :class="{ 'table-button-active': currentPage === p }"
      />
    </template>

    <base-button
      id="table-next"
      class-name="table-button"
      btn-type="tertiary"
      :functions="nextPage"
      :disabled="currentPage === totalPages"
      name="table-next"
      text="Next"
    >
      <template #contents>
        <i class="fa-solid fa-chevron-right"></i>
      </template>
    </base-button>
  </div>
</template>

<style lang="scss" scoped>
.table {
  @apply mt-12 mb-4 border-collapse text-sm max-w-[55rem];
  &-sortBtn {
    @apply w-auto text-greys-300;
    &:hover {
      @apply text-blacks-500;
    }
  }
  &-headers {
    @apply text-left text-p-sm text-blacks-500 font-bold pb-4 last:px-4 md:last:px-0 capitalize;
  }
  &-picker {
    @apply pl-20;
  }
  &-items {
    @apply border-b border-t h-[4.5rem];
    &-container {
      @apply md:ml-0 ml-4 flex flex-row w-full justify-between;
    }
    &-selected {
      @apply bg-gray-200;
    }
  }
  &-button {
    @apply w-8 h-8 p-0 leading-none text-blacks-500 text-sm rounded-full;
  }
  &-pagination {
    @apply w-full flex items-center justify-center gap-3 mt-2;
  }
  &-ellipsis {
    @apply px-1 text-greys-300 text-sm select-none;
  }
  &-button-active {
    @apply font-bold text-background-500;
  }
  // When fullWidth is true, allow the table to span the full width by removing max-width constraint
  &.w-full {
    @apply max-w-none;
  }
}
</style>
